import { useState } from 'react';
import { OpenDisputeModal } from '@/components/features/disputes/OpenDisputeModal';
import { milestoneBudget } from '@/lib/project-presentation';
import { SubmitWorkAction } from '@/components/features/milestones/SubmitWorkAction';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import type { MilestoneView, ProjectView } from '@/types/current-project';
import type { useProjectCommand } from '@/hooks/useProjectCommand';
import { projectDateLabel } from '@/lib/project-presentation';
import { useProposalPresentation } from '@/hooks/useProposalReviews';

type Evidence = { url: string };
export function EvidenceFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Evidence;
  onChange: (value: Evidence) => void;
}) {
  return (
    <div className="space-y-3">
      <label>
        {label} HTTPS URL
        <input
          type="url"
          aria-label={label + ' HTTPS URL'}
          required
          value={value.url}
          onChange={(e) => onChange({ ...value, url: e.target.value })}
        />
      </label>
    </div>
  );
}
export function EvidenceLink({
  label,
  evidence,
}: {
  label: string;
  evidence: Evidence | null;
}) {
  if (!evidence)
    return (
      <p className="text-sm text-muted-foreground">
        {label}: no supporting link provided.
      </p>
    );
  return (
    <div className="space-y-1 break-all text-sm">
      <a
        className="text-primary underline"
        href={evidence.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}: {evidence.url}
      </a>
    </div>
  );
}

export function ProjectCommandFeedback({
  command,
}: {
  command: ReturnType<typeof useProjectCommand>;
}) {
  return (
    <div className="space-y-3">
      {command.error && (
        <p role="alert">
          {command.error} Review refreshed server data before trying again.
        </p>
      )}
      {command.status && <p role="status">{command.status}</p>}
      {command.pending && (
        <Button
          disabled={command.busy || command.blocked}
          onClick={() => void command.run(null)}
        >
          Check pending project request
        </Button>
      )}
    </div>
  );
}

export function MilestoneCompletion({
  project,
  milestone,
  command,
}: {
  project: ProjectView;
  milestone: MilestoneView;
  command: ReturnType<typeof useProjectCommand>;
  review?: boolean;
}) {
  const account = useAuthStore((s) => s.session?.accountId);
  const pending = milestone.submissions.find(
    (s) => s.review.type === 'PendingReview'
  );
  const active =
    !project.cancelled &&
    !project.completed &&
    !project.activeDisputeId &&
    !milestone.frozen;
  const submit =
    active &&
    account === project.coordinator &&
    ['InProgress', 'ChangesRequested'].includes(milestone.status ?? '');
  const canReview =
    active &&
    account === project.client &&
    milestone.status === 'CompletionRequested' &&
    pending;
  const locked = command.busy || command.pending || command.blocked;
  const canOpen = canOpenDispute(project, milestone, account);
  return (
    <section
      className={
        submit
          ? 'space-y-4 text-left'
          : 'backendForm space-y-4 rounded-[--radi-6] border border-input bg-card p-5 text-left sm:p-6'
      }
      aria-label={'Delivery for ' + milestone.definition.title}
    >
      {!submit && (
        <>
          <h3 className="text-lg font-semibold">
            Milestone delivery · {milestone.status ?? 'Not activated'}
          </h3>
          <p className="text-sm text-muted-foreground">
            Task tracking does not release payment. The coordinator submits a
            versioned delivery for the client to review. Rejection requests
            changes and keeps escrow held; disputes are a separate workflow.
          </p>
          {!milestone.submissions.length && <p>No deliveries submitted.</p>}
          {milestone.submissions.map((s) => (
            <article
              key={s.submissionId}
              className="space-y-2 border-t border-input pt-4"
            >
              <h4 className="font-semibold">
                Delivery version {s.version} ·{' '}
                {
                  {
                    PendingReview: 'Awaiting review',
                    Rejected: 'Changes requested',
                    RejectedWithComment: 'Changes requested',
                    Accepted: 'Accepted',
                  }[s.review.type]
                }
              </h4>
              <p className="break-all text-xs text-muted-foreground">
                Submission {s.submissionId} · Submitted by {s.submittedBy} ·{' '}
                {projectDateLabel(s.submittedAt)}
              </p>
              <EvidenceLink label="Deliverable" evidence={s.deliverable} />
              {s.workerRatings.map((r) => (
                <p key={r.worker} className="break-all text-sm">
                  Worker {r.worker}: {r.score}/5
                </p>
              ))}
              {s.review.type === 'Rejected' && (
                <EvidenceLink
                  label="Requested changes"
                  evidence={s.review.reason}
                />
              )}
              {s.review.type !== 'PendingReview' && (
                <p className="break-all text-xs text-muted-foreground">
                  Reviewed by {s.review.reviewedBy} ·{' '}
                  {projectDateLabel(s.review.reviewedAt)}
                </p>
              )}
            </article>
          ))}
        </>
      )}
      {account === project.coordinator && (
        <SubmitWorkAction
          project={project}
          milestone={milestone}
          command={command}
          canSubmit={submit}
        />
      )}
      {(canReview || (canOpen && account === project.client)) && (
        <Link
          className="text-primary underline"
          to={
            '/projects/' +
            project.projectId +
            '/milestones/' +
            milestone.milestoneId +
            '/review'
          }
        >
          Review milestone delivery
        </Link>
      )}
      <OpenDisputeAction
        project={project}
        milestone={milestone}
        disabled={locked}
      />
      {project.activeDisputeId && (
        <Link
          className="break-all text-primary underline"
          to={'/disputes/' + project.activeDisputeId}
        >
          View public dispute
        </Link>
      )}
    </section>
  );
}

function canOpenDispute(
  project: ProjectView,
  milestone: MilestoneView,
  account?: string
) {
  const rejected = milestone.submissions[milestone.submissions.length - 1];
  return (
    !project.cancelled &&
    !project.completed &&
    !project.activeDisputeId &&
    !milestone.frozen &&
    (account === project.client || account === project.coordinator) &&
    milestone.status === 'ChangesRequested' &&
    !!rejected &&
    ['Rejected', 'RejectedWithComment'].includes(rejected.review.type)
  );
}

export function OpenDisputeAction({
  project,
  milestone,
  disabled,
}: {
  project: ProjectView;
  milestone: MilestoneView;
  disabled: boolean;
}) {
  const account = useAuthStore((s) => s.session?.accountId);
  const [opening, setOpening] = useState(false);
  const proposal = project.proposals.find((p) =>
    p.milestones.some((m) => m.milestoneId === milestone.milestoneId)
  );
  const presentation = useProposalPresentation(
    project.projectId,
    proposal?.proposalId
  );
  const description = presentation.data?.matchesCurrentDefinition
    ? (presentation.data.milestones.find(
        (m) => m.key === milestone.definition.key
      )?.description ?? '')
    : '';
  if (!canOpenDispute(project, milestone, account)) return null;
  return (
    <>
      <div className="flex flex-col justify-between gap-2 rounded-lg border border-rose-500/20 bg-rose-500/5 p-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-1.5 text-xs text-rose-300">
          <i className="ri-error-warning-line shrink-0" />
          <span>Submission rejected by client.</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || presentation.isLoading || presentation.isError}
          onClick={() => setOpening(true)}
          className="shrink-0 self-start border-amber-500/40 text-xs text-amber-300 hover:bg-amber-500/10 sm:self-auto"
        >
          <i className="ri-scales-3-line mr-1" />
          Open Dispute
        </Button>
      </div>
      {presentation.isError && (
        <p role="alert" className="text-xs text-rose-300">
          Could not load milestone details.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void presentation.refetch()}
          >
            Retry
          </button>
        </p>
      )}
      {opening && (
        <OpenDisputeModal
          key={
            milestone.submissions[milestone.submissions.length - 1]!
              .submissionId
          }
          isOpen
          onClose={() => setOpening(false)}
          project={{ id: project.projectId, title: project.title }}
          milestone={{
            id: milestone.milestoneId,
            title: milestone.definition.title,
            description,
            budget: milestoneBudget(milestone),
          }}
          userRole={account === project.client ? 'client' : 'consultant'}
        />
      )}
    </>
  );
}
